import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import 'date-fns';
import { useFormik } from 'formik';
import {
  Row,
  RightContainer,
  TextField,
  SubmitButton,
  TextArea,
  Select,
  ImagePicker
} from 'components/CreationForm';
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera';
import { useRowsStyles } from '../RowsStyles';
import { useStyles } from './RightStyles';
import { messages } from './RightMessages';
import { useLoader } from '@octopy/react-loader';
import { useModal } from '@octopy/react-modal';
import { createErrorModalConfig, getDataError } from 'utils/helpers';
import { useDashboard } from 'components/Dashboard';
import { Hidden, Radio } from '@material-ui/core';
import { getProductById } from 'providers/api';
import { getValidationSchema } from './helper';
import { Images } from 'assets';

const Right = ({ history }) => {
  const rowsClasses = useRowsStyles();
  const classes = useStyles();
  const intl = useIntl();
  const { handleOpenModal } = useModal();
  const { handleShowLoader } = useLoader();
  const { updateExhibitionById, events } = useDashboard();
  const [productDetails, setProductDetails] = useState({});
  const [optionsForSelect, setOptionsForSelect] = useState([]);
  const [idEvent, setIdEvent] = useState('');
  const [hostVideo, sethostVideo] = useState('');
  const { idProduct } = useParams();

  useEffect(async () => {
    try {
      handleShowLoader(true);
      const productData = await getProductById(idProduct);

      setProductDetails({
        ...productData,
        positionInArray: history.location.state.positionInArray
      });

      const options = events.map((event) => ({
        value: event.id,
        label: event.name
      }));

      sethostVideo(productData.plataform);
      setIdEvent(productData.event);
      setOptionsForSelect(options);
    } catch (request) {
      const { code, message } = getDataError(request);

      handleOpenModal(createErrorModalConfig(code, message));
      history.push('/Exhibition');
    } finally {
      handleShowLoader(false);
    }
  }, []);

  const handleSetHostVideo = (event) => {
    sethostVideo(event.target.value);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      logoUrl: productDetails.image,
      videoId: productDetails.videoId,
      urlChat: productDetails.chat,
      productName: productDetails.name,
      description: productDetails.description,
      slogan: productDetails.slogan,
      event: idEvent
    },
    validationSchema: getValidationSchema(intl),
    onSubmit: async (values) => {
      const dataToSend = { ...productDetails, ...values, plataform: hostVideo };

      updateExhibitionById(dataToSend, productDetails.positionInArray);

      history.goBack();
    }
  });

  const submitButton = (
    <Row>
      <SubmitButton
        label={intl.formatMessage(messages.saveChanges)}
        onClick={formik.handleSubmit}
      />
    </Row>
  );

  return (
    <RightContainer submitButton={submitButton}>
      <Row className={rowsClasses.multimedia}>
        <Row className={classes.fullwidth}>
          <ImagePicker
            id="editExhibition"
            onChange={(logo) => {
              formik.setFieldValue('logoUrl', logo.blobUrl);
              formik.setFieldValue('logo', logo);
            }}
            error={
              formik.errors['logoUrl'] && !!!formik.values.logoUrl
                ? formik.errors['logoUrl']
                : undefined
            }
            Icon={PhotoCameraIcon}
            label={intl.formatMessage(messages.logoProductLabel)}
            name="logoUrl"
            imageUrl={formik.values.logoUrl || formik.values.logo?.blobUrl}
          />
        </Row>
        <div className={classes.multimediaRow}>
          <div className={classes.containerVideo}>
            <TextField
              className={classes.input}
              key={`videoIdInput`}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.errors['videoId']}
              value={formik.values['videoId']}
              touched={formik.touched['videoId']}
              label={intl.formatMessage(messages.videoId)}
              name={'videoId'}
            />
            <div className={`${classes.input} ${classes.radioGroup}`}>
              <Radio
                size="small"
                checked={hostVideo === 'youtube'}
                onChange={handleSetHostVideo}
                value="youtube"
                name="youtube"
              />
              <img
                src={Images.youtube}
                alt="youtube"
                className={classes.platformLogoImage}
              />
              &nbsp;&nbsp;
              <Radio
                size="small"
                checked={hostVideo === 'vimeo'}
                onChange={handleSetHostVideo}
                value="vimeo"
                name="vimeo"
              />
              <img
                src={Images.vimeo}
                alt="vimeo"
                className={classes.platformLogoImage}
              />
            </div>
          </div>
          <TextField
            className={classes.input}
            key={`urlChatInput`}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors['urlChat']}
            value={formik.values['urlChat']}
            touched={formik.touched['urlChat']}
            label={intl.formatMessage(messages.urlChatLabel)}
            name={'urlChat'}
          />
        </div>
      </Row>
      <Row className={rowsClasses.product}>
        <TextField
          className={classes.input}
          key={`productNameInput`}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors['productName']}
          value={formik.values['productName']}
          touched={formik.touched['productName']}
          label={intl.formatMessage(messages.productName)}
          name={'productName'}
        />
        <TextField
          className={classes.input}
          key={`slogan`}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors['slogan']}
          value={formik.values['slogan']}
          touched={formik.touched['slogan']}
          label={intl.formatMessage(messages.productSlogan)}
          name={'slogan'}
        />
        <Hidden smDown>
          <div style={{ flexGrow: 1 }} />
        </Hidden>
        <TextArea
          key={`description`}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors['description']}
          value={formik.values['description']}
          touched={formik.touched['description']}
          className={classes.input}
          rowsMin={1}
          placeholder={intl.formatMessage(messages.productDescription)}
          name={`description`}
        />
      </Row>
      <Row>
        <Select
          className={classes.input}
          key={`event`}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors['event']}
          value={formik.values['event'] || idEvent}
          touched={formik.touched['event']}
          label={intl.formatMessage(messages.selectEvent)}
          name={'event'}
          options={optionsForSelect}
        />
      </Row>
    </RightContainer>
  );
};

export { Right };
